import { useEffect, useState } from 'react';
import { parseISO, differenceInDays, startOfDay } from 'date-fns';

import { useSelector } from 'react-redux';
import { userRoleData } from 'redux_store/slices/userRole';

import {
  useGetAllChatsQuery,
  useGetChatMessagesQuery,
  useCreateNewChatMutation,
} from '../../redux/services/borrower-profile';

import ChatHistoryBtn from './ChatHistoryBtn';
import ChatHistoryModal from './ChatHistoryModal';
import styles from '../../styles/chat-history.module.css';

const ChatHistory = ({
  userId,
  organizationId,
  isB2C = false,
  isMobile = false,
  isMobileChatOpen = false,
  isTabletChatHidden = false,
  setMessages,
  isVectorClicks,
  setChatIds,
  setVectorClicks,
  setIsNewChat,
  setNewChatOpens,
  isNewChatOpens,
  isChatExpanded,
  setChatHistorySelected,
  setNewChatSelected,
}) => {
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [isNewChatClicked, setIsNewChatclicked] = useState(false);
  const [selectedChatID, setSelectedChatID] = useState(null);
  const [groupedChats, setGroupedChats] = useState([]);

  const role = useSelector(userRoleData);

  //RTK query to get all chats
  const { data: allchatHistory, refetch: refetchAllChatHistory } =
    useGetAllChatsQuery(
      { isB2C },
      { skip: !isChatOpen && !isVectorClicks }
    );

  //RTK query to get the chat messages details
  const { data: getChatMessages, refetch: refetchMessages } =
    useGetChatMessagesQuery(
      { chatId: selectedChatID, isB2C },
      {
        skip: !selectedChatID,
      }
    );

  const [createNewChat, { data: newchatData }] = useCreateNewChatMutation({
    skip: !isNewChatClicked || !isNewChatOpens,
  });

  // Normalize chat message history from backend_v2 AI chat endpoints
  useEffect(() => {
    if (getChatMessages) {
      const transformedMessages = getChatMessages?.map((message) => {
        const role = (message?.role || '').toLowerCase();
        const sender = role === 'user' ? 'user' : 'AI';
        const content = message?.content ?? '';
        return { sender, content };
      });
      setMessages(transformedMessages);
    }
  }, [getChatMessages, setMessages]);

  useEffect(() => {
    if (isVectorClicks) {
      onChatHistoryClicks();
    }
  }, [isVectorClicks]);

  //Update grouped chats when allchatHistory changes
  useEffect(() => {
    if (allchatHistory) {
      setGroupedChats(groupChatsByRelativeTime(allchatHistory));
    }
  }, [allchatHistory]);

  useEffect(() => {
    if (!isTabletChatHidden) {
      return;
    }
    setIsChatOpen(false);
    setVectorClicks(false);
    setNewChatOpens(false);
  }, [isTabletChatHidden, setNewChatOpens, setVectorClicks]);

  //Function to group the chats by date
  const groupChatsByRelativeTime = (chats) => {
    const now = startOfDay(new Date());
    const groupedChats = {
      Today: [],
      Yesterday: [],
      Earlier: [],
    };
    chats?.forEach((chat) => {
      const timestamp =
        chat?.last_message_at ||
        chat?.created_at ||
        chat?.start_time ||
        chat?.createdAt;
      if (!timestamp) {
        return;
      }

      const normalizedChat = {
        chat_id: chat?.chat_id || chat?.id || chat?.session_id || chat?.sessionId,
        title: chat?.title,
        created_at: timestamp,
      };

      const chatDate = startOfDay(parseISO(timestamp));
      const daysDifference = differenceInDays(now, chatDate);
      if (daysDifference === 0) {
        groupedChats.Today.push(normalizedChat);
      } else if (daysDifference === 1) {
        groupedChats.Yesterday.push(normalizedChat);
      } else {
        groupedChats.Earlier.push(normalizedChat);
      }
    });
    return groupedChats;
  };

  //Function that triggers when the Chat History button clicks
 //Function that triggers when the Chat History button clicks
const onChatHistoryClicks = async () => {
  if (isChatOpen) {
    // If already open, close it
    setIsChatOpen(false);
    setVectorClicks(false);
    setNewChatOpens(false);
  } else {
    // If closed, open it
    setNewChatSelected(false);
    setNewChatOpens(true);
    setIsChatOpen(true);
    try {
      const { data } = await refetchAllChatHistory();
      if (data) {
        const updatedGroupedChats = groupChatsByRelativeTime(data);
        setGroupedChats(updatedGroupedChats);
      }
    } catch (error) {
      console.error('Error fetching chat history:', error);
    }
  }
};

  //Function that triggers when selected a particular chat
  const setChatWindowOpens = (chatId) => {
    setChatHistorySelected(true);
    setSelectedChatID(chatId);
    if (setChatIds) {
      setChatIds(chatId);
    }
    setIsChatOpen(false);
    setVectorClicks(false);
    setNewChatOpens(false);
    setMessages([]);
  };

  //function that triggers when the New Chat clicks
  const newChatAction = () => {
    setIsNewChatclicked(true);
    setIsNewChat(true);
    setIsChatOpen(false);
    setVectorClicks(false);
    setMessages([]);
    setChatHistorySelected(false);
    if (setChatIds) {
      setChatIds(null);
    }
  };

  const wrapperClassName = `${styles.historyWrapper} ${
    isChatExpanded ? styles.historyExpandedOffset : ''
  } ${isMobile ? styles.historyMobileWrapper : ''} ${
    isMobile && isChatOpen ? styles.historyMobileOpen : ''
  } ${
    isVectorClicks && isChatOpen
      ? styles.historyTopVectorOpen
      : isChatOpen
        ? styles.historyTopOpen
        : styles.historyTopClosed
  }`;
  const wrapperStyle = !isMobile && isB2C
    ? {
        right: 'calc(var(--chat-aside-current-width, var(--chat-aside-width, 450px)) - 3px)',
      }
    : undefined;

  if ((isMobile && !isMobileChatOpen) || isTabletChatHidden) {
    return null;
  }

  return (
    <div className={wrapperClassName} style={wrapperStyle}>
      {(!isChatOpen || isMobile) && (
        <ChatHistoryBtn
          role={role?.userRole}
          isMobile={isMobile}
          isChatOpen={isChatOpen}
          onChatHistoryClicks={onChatHistoryClicks}
          allchatHistory={allchatHistory}
        />
      )}

      <ChatHistoryModal
        isMobile={isMobile}
        isChatOpen={isChatOpen}
        isVectorClicks={isVectorClicks}
        setIsChatOpen={setIsChatOpen}
        newChatAction={newChatAction}
        setChatWindowOpens={setChatWindowOpens}
        groupedChats={groupedChats}
        setVectorClicks={setVectorClicks}
        setNewChatOpens={setNewChatOpens}
        setNewChatSelected={setNewChatSelected}
      />
    </div>
  );
};

export default ChatHistory;
